import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputTextModule } from 'primeng/inputtext';
import { SidebarModule } from 'primeng/sidebar';
import { DialogModule } from 'primeng/dialog';

export interface FilterFieldConfig {
  /** Unique field name — used as FormControl key */
  name: string;
  /** Display label */
  label: string;
  /** Field type */
  type: 'calendar' | 'dropdown' | 'text' | 'multi-select';
  /** Default value (restored on Clear All) */
  defaultValue?: any;
  /** Dropdown / multi-select options: { name, value } */
  options?: any[];
  /** optionValue key for p-multiSelect (e.g. 'name' or 'code') */
  optionValue?: string;
  /** optionDisabled key for p-multiSelect (e.g. 'inactive') */
  optionDisabled?: string;
  /** Number of labels shown before collapsing to a count */
  maxSelectedLabels?: number;
  /** Placeholder text */
  placeholder?: string;
  /** Calendar date format (default: 'yy-mm-dd') */
  dateFormat?: string;
  /** Calendar min date */
  minDate?: Date;
  /** Calendar max date */
  maxDate?: Date;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether to show a chip for this field (default true) */
  showChip?: boolean;
  /** Whether the field is required for submission */
  required?: boolean;
}

@Component({
  selector: 'app-filter-sidebar',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, CalendarModule, DropdownModule, MultiSelectModule, InputTextModule, SidebarModule, DialogModule],
  templateUrl: './filter-sidebar.component.html',
  styleUrls: ['./filter-sidebar.component.scss']
})
export class FilterSidebarComponent implements OnChanges {
  @Input() fields: FilterFieldConfig[] = [];
  @Input() title = 'text.filterSidebarTitle';
  @Input() hint = 'text.filterSidebarHint';
  @Input() submitLabel = 'text.submitLabel';
  @Input() visible = false;
  /** Active filter values — kept in sync with the parent (e.g. after chip removal) */
  @Input() activeFilters: Record<string, any> = {};
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() filterSubmit = new EventEmitter<Record<string, any>>();
  @Output() filterClear = new EventEmitter<Record<string, any>>();
  @Output() fieldChanged = new EventEmitter<{ fieldName: string; value: any; formValues: Record<string, any> }>();

  filterForm: FormGroup;
  showValidationDialog = false;
  validationMessage = '';

  constructor(private fb: FormBuilder) {
    this.filterForm = this.fb.group({});
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields']?.currentValue) {
      const previousFormValues = this.filterForm?.getRawValue?.() || {};
      this.buildForm();
      // After rebuilding the form, restore any active filter values
      if (this.activeFilters && Object.keys(this.activeFilters).length > 0) {
        this.patchFormFromFilters(this.activeFilters);
      }
      // Preserve unsaved in-progress selections while parent rebuilds field metadata.
      this.patchFormFromFilters(previousFormValues);
    }
    if (changes['activeFilters']?.currentValue && this.filterForm) {
      this.patchFormFromFilters(changes['activeFilters'].currentValue);
    }
    // Rehydrate controls from active filters every time the sidebar is opened.
    if (changes['visible']?.currentValue === true && this.filterForm) {
      this.patchFormFromFilters(this.activeFilters || {});
    }
  }

  /** Patch form controls to match activeFilters coming from parent */
  private patchFormFromFilters(filters: Record<string, any>): void {
    this.fields.forEach(field => {
      const control = this.filterForm.get(field.name);
      if (control) {
        // If field is absent from filters, clear it to null (don't restore default)
        const raw = field.name in filters ? (filters[field.name] ?? null) : null;
        const value = this.normaliseFieldValue(field, raw);
        if (control.value !== value) {
          control.setValue(value, { emitEvent: false });
        }
      }
    });
  }

  private getOptionValueKey(field: FilterFieldConfig): string {
    return field.optionValue || 'value';
  }

  private normaliseFieldValue(field: FilterFieldConfig, value: any): any {
    const optionValueKey = this.getOptionValueKey(field);

    if (field.type === 'multi-select' && Array.isArray(value)) {
      return value.map(item => {
        if (item && typeof item === 'object') {
          return item[optionValueKey] ?? item.value ?? item.code ?? item;
        }
        return item;
      });
    }

    if (field.type === 'dropdown' && value && typeof value === 'object') {
      return value[optionValueKey] ?? value.value ?? value.code ?? value;
    }

    return value;
  }

  private buildForm(): void {
    this.filterForm = this.fb.group({});
    this.fields.forEach(field => {
      const initialValue = this.normaliseFieldValue(field, field.defaultValue ?? null);
      const control = this.fb.control(
        { value: initialValue, disabled: field.disabled ?? false }
      );
      this.filterForm.addControl(field.name, control);
    });
  }

  onFieldPanelHide(fieldName: string): void {
    if (fieldName !== 'Report_Category') {
      return;
    }

    // Keep previous behavior: changing category resets selected report types.
    this.filterForm.get('Report_Type')?.setValue([], { emitEvent: false });

    const value = this.filterForm.get(fieldName)?.value ?? [];
    this.fieldChanged.emit({ fieldName, value, formValues: this.filterForm.getRawValue() });
  }

  clearAll(): void {
    this.fields.forEach(field => {
      const control = this.filterForm.get(field.name);
      if (control && !field.disabled) {
        control.setValue(null);
      }
    });
    // this.filterClear.emit(this.filterForm.getRawValue());
  }

  submit(): void {
    const values = this.filterForm.getRawValue();

    // Check if at least one filter has a value
    const hasValue = Object.values(values).some(val =>
      val != null && val !== '' && !(Array.isArray(val) && val.length === 0)
    );
    // if (!hasValue) {
    //   this.validationMessage = 'Please select any one value in filter.';
    //   this.showValidationDialog = true;
    //   return;
    // }

    // Validate required fields
    const missingRequired = this.fields.filter(field =>
      field.required && !this.hasSidebarValue(values[field.name])
    );
    if (missingRequired.length > 0) {
      this.validationMessage = 'Please select ' + missingRequired.map(f => f.label).join(', ') + '.';
      this.showValidationDialog = true;
      return;
    }

    // Validate date range if both From and To dates are present
    const fromDate = values['From_Date'];
    const toDate = values['To_Date'];
    if (fromDate instanceof Date && toDate instanceof Date && fromDate > toDate) {
      this.validationMessage = 'Please select valid date range.';
      this.showValidationDialog = true;
      return;
    }

    this.filterSubmit.emit(values);
    this.close();
  }

  private hasSidebarValue(value: any): boolean {
    return value != null && value !== '' && !(Array.isArray(value) && value.length === 0);
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onOverlayClick(): void {
    this.close();
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (this.visible) {
      this.close();
    }
  }
}
